import { useState, type FormEvent } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { createQuote } from "./API/quotes";
import { useTags } from "../Tags/Hooks/useTags";
import { TagEditor } from "../Tags/TagEditor";

type QuoteFormProps = {
  onSubmitted?: () => void;
};

export function QuoteForm({ onSubmitted }: QuoteFormProps) {
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [source, setSource] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const { tags, tagInput } = useTags();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");

    if (!content.trim()) {
      setMessage("Quote content is required.");
      return;
    }

    setSubmitting(true);

    try {
      await createQuote({
        content: content.trim(),
        author: author.trim() || null,
        source: source.trim() || null,
        tags: tags.list
      });

      setContent("");
      setAuthor("");
      setSource("");
      tags.reset();
      setMessage("Quote submitted.");
      onSubmitted?.();
    } catch (error) {
      console.error("Error submitting quote:", error);
      setMessage("Unable to submit quote.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <TagEditor
        open={modalOpen}
        tagInput={tagInput.value}
        tags={tags.list}
        onOpen={() => setModalOpen(true)}
        onClose={() => setModalOpen(false)}
        onTagInputChange={tagInput.update}
        onAddTag={tags.add}
        onRemoveTag={tags.remove}
      />
      
      <Box component="form" onSubmit={handleSubmit}>
        <Stack direction={{ xs: "column", sm: "row" }} sx={{ gap: 2, mb: 2 }}>
          <TextField
            label="Author"
            value={author}
            onChange={(event) => setAuthor(event.target.value)}
            fullWidth
            sx={textFieldSx}
          />
          <TextField
            label="Source"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            fullWidth
            sx={textFieldSx}
          />
        </Stack>

        <TextField
          value={content}
          onChange={(event) => setContent(event.target.value)}
          multiline
          minRows={10}
          fullWidth
          required
          placeholder="Write or paste the quote here..."
          sx={{
            ...textFieldSx,
            mb: 2,
            "& .MuiInputBase-root": {
              alignItems: "flex-start",
              color: "white",
              fontSize: "1rem",
              lineHeight: 1.6,
              p: 1,
            },
            "& textarea": {
              color: "white",
              resize: "vertical",
            },
          }}
        />

        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit"}
          </Button>
          {message ? <Typography sx={{ color: "white" }}>{message}</Typography> : null}
        </Stack>
      </Box>
    </>
  );
}

const textFieldSx = {
  "& .MuiInputBase-input": {
    color: "white",
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.55)",
  },
};
